import { useState, useEffect } from "react";
import { format } from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { CalendarDays, Clock, Loader2, CheckCircle } from "lucide-react";
import { normalizeUrl } from "@/lib/urlUtils";
import { AddressFields } from "@/components/AddressFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = ["00", "15", "30", "45"];
const EMPTY_ORGANIZATIONS = [];

const parseTimeStr = (timeStr) => {
  if (!timeStr) return { hour: "12", minute: "00", period: "PM" };
  const match = timeStr.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM|am|pm)?/);
  if (!match) return { hour: "12", minute: "00", period: "PM" };
  return {
    hour: match[1] || "12",
    minute: match[2] || "00",
    period: (match[3] || "PM").toUpperCase(),
  };
};

const parseDateStr = (dateStr) => {
  if (!dateStr) return undefined;
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? undefined : d;
  } catch {
    return undefined;
  }
};

const getHostName = (organization, hostType) => (
  hostType === "market" ? organization?.market_name : organization?.business_name
);

const getHostEmail = (organization, hostType) => (
  hostType === "market" ? organization?.email : organization?.business_email
);

export const EventFormModal = ({
  open,
  onClose,
  token,
  onSuccess,
  editEvent = null,
  adminMode = false,
  businesses = EMPTY_ORGANIZATIONS,
  markets = EMPTY_ORGANIZATIONS,
  hostId = "",
}) => {
  const isEditing = !!editEvent;
  const [form, setForm] = useState({ title: "", description: "", location: "", event_link: "" });
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [time, setTime] = useState({ hour: "12", minute: "00", period: "PM" });
  const [endTime, setEndTime] = useState({ hour: "1", minute: "00", period: "PM" });
  const [hasEndTime, setHasEndTime] = useState(false);
  const [hostType, setHostType] = useState("admin");
  const [selectedHostId, setSelectedHostId] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);

  useEffect(() => {
    if (editEvent) {
      const normalizedType = editEvent.submitted_by_type === "business" || editEvent.submitted_by_type === "market"
        ? editEvent.submitted_by_type
        : "admin";
      const hostList = normalizedType === "market" ? markets : businesses;
      const matchingHost = hostList.find((organization) => (
        getHostEmail(organization, normalizedType) === editEvent.submitted_by_email
        || getHostName(organization, normalizedType) === editEvent.submitted_by_name
      ));
      setForm({
        title: editEvent.title || "",
        description: editEvent.description || "",
        location: editEvent.location || "",
        event_link: editEvent.event_link || "",
      });
      setSelectedDate(parseDateStr(editEvent.date));
      setTime(parseTimeStr(editEvent.time));
      setEndTime(parseTimeStr(editEvent.end_time));
      setHasEndTime(!!editEvent.end_time);
      setHostType(normalizedType);
      setSelectedHostId(matchingHost?.id || "");
    } else {
      setForm({ title: "", description: "", location: "", event_link: "" });
      setSelectedDate(undefined);
      setTime({ hour: "12", minute: "00", period: "PM" });
      setEndTime({ hour: "1", minute: "00", period: "PM" });
      setHasEndTime(false);
      setHostType("admin");
      setSelectedHostId("");
    }
    setSubmitted(false);
  }, [editEvent, open, businesses, markets]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const getFormattedDate = () => selectedDate ? format(selectedDate, "MMMM d, yyyy") : "";
  const getFormattedTime = () => `${time.hour}:${time.minute} ${time.period}`;
  const getFormattedEndTime = () => hasEndTime ? `${endTime.hour}:${endTime.minute} ${endTime.period}` : "";

  const getSelectedHost = () => {
    const hostList = hostType === "market" ? markets : businesses;
    return hostList.find((organization) => organization.id === selectedHostId);
  };

  const handleHostTypeChange = (value) => {
    setHostType(value);
    setSelectedHostId("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Event title is required"); return; }
    if (adminMode && hostType !== "admin" && !selectedHostId) {
      toast.error(`Select a ${hostType === "market" ? "market" : "vendor"} for this event`);
      return;
    }
    setLoading(true);
    const payload = {
      title: form.title,
      description: form.description,
      date: getFormattedDate(),
      time: getFormattedTime(),
      end_time: getFormattedEndTime(),
      location: form.location,
      event_link: normalizeUrl(form.event_link),
    };
    if (adminMode) {
      const host = getSelectedHost();
      payload.approval_status = editEvent?.approval_status || "approved";
      payload.submitted_by_type = hostType;
      payload.submitted_by_name = host ? getHostName(host, hostType) : "614 Shop Local";
      payload.submitted_by_email = host ? getHostEmail(host, hostType) : null;
    }
    try {
      if (isEditing) {
        const endpoint = adminMode ? `${API}/admin/event/${editEvent.id}` : `${API}/events/${editEvent.id}`;
        await axios.put(endpoint, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Event updated!");
      } else {
        const ownerHostQuery = hostId ? `?host_id=${encodeURIComponent(hostId)}` : "";
        const endpoint = adminMode ? `${API}/admin/event` : `${API}/events/submit${ownerHostQuery}`;
        await axios.post(endpoint, payload, { headers: { Authorization: `Bearer ${token}` } });
        if (!adminMode) setSubmitted(true);
        toast.success(adminMode ? "Event created!" : "Event submitted for approval!");
      }
      onSuccess();
      if (isEditing || adminMode) onClose();
    } catch (error) { toast.error(error.response?.data?.detail || `Failed to ${isEditing ? "update" : "submit"} event`); }
    finally { setLoading(false); }
  };

  const handleClose = () => {
    setSubmitted(false);
    setForm({ title: "", description: "", location: "", event_link: "" });
    setSelectedDate(undefined);
    setTime({ hour: "12", minute: "00", period: "PM" });
    setEndTime({ hour: "1", minute: "00", period: "PM" });
    setHasEndTime(false);
    setHostType("admin");
    setSelectedHostId("");
    onClose();
  };

  const hostOptions = hostType === "market" ? markets : businesses;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogTitle className="text-xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {isEditing ? "Edit Event" : adminMode ? "Add Event" : "Submit an Event"}
        </DialogTitle>
        <DialogDescription className="text-gray-500 text-sm">
          {adminMode
            ? "Create or update the event details below."
            : isEditing ? "Update your event details below." : "Events require admin approval before being published."}
        </DialogDescription>
        {submitted && !isEditing ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-[#42D78A]/10 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-8 h-8 text-[#42D78A]" /></div>
            <h3 className="text-lg font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Event Submitted!</h3>
            <p className="text-gray-500 text-sm mb-6">Pending admin approval.</p>
            <Button onClick={handleClose} className="rounded-full bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] font-bold px-8">Done</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium">Event Title *</Label>
              <Input value={form.title} onChange={(e) => handleChange("title", e.target.value)} placeholder="Grand Opening Sale" className="h-11 mt-1 rounded-lg" data-testid="event-title-input" />
            </div>
            <div>
              <Label className="text-sm font-medium">Description</Label>
              <Textarea value={form.description} onChange={(e) => handleChange("description", e.target.value)} placeholder="What's the event about?" className="mt-1 rounded-lg min-h-[80px]" data-testid="event-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {/* Date Picker */}
              <div>
                <Label className="text-sm font-medium">Date</Label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={`w-full h-11 mt-1 rounded-lg justify-start text-left font-normal ${!selectedDate && "text-muted-foreground"}`} data-testid="event-date-picker">
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, "MMM d, yyyy") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={selectedDate} onSelect={(d) => { setSelectedDate(d); setDateOpen(false); }} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              {/* Time Picker */}
              <div>
                <Label className="text-sm font-medium">Time</Label>
                <div className="flex items-center gap-1 mt-1">
                  <Select value={time.hour} onValueChange={(v) => setTime(p => ({ ...p, hour: v }))}>
                    <SelectTrigger className="h-11 rounded-lg w-[68px]" data-testid="event-time-hour">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>{HOURS.map(h => <SelectItem key={h} value={String(h)}>{h}</SelectItem>)}</SelectContent>
                  </Select>
                  <span className="text-lg font-bold text-gray-400">:</span>
                  <Select value={time.minute} onValueChange={(v) => setTime(p => ({ ...p, minute: v }))}>
                    <SelectTrigger className="h-11 rounded-lg w-[68px]" data-testid="event-time-minute">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>{MINUTES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={time.period} onValueChange={(v) => setTime(p => ({ ...p, period: v }))}>
                    <SelectTrigger className="h-11 rounded-lg w-[72px]" data-testid="event-time-period">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AM">AM</SelectItem>
                      <SelectItem value="PM">PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between gap-3">
                <Label className="text-sm font-medium">End Time</Label>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setHasEndTime((value) => !value)}
                  className="h-7 px-2 text-xs text-slate-500 hover:text-slate-900"
                  data-testid="event-end-time-toggle"
                >
                  {hasEndTime ? "Remove" : "Add end time"}
                </Button>
              </div>
              {hasEndTime && (
                <div className="flex items-center gap-1">
                  <Select value={endTime.hour} onValueChange={(v) => setEndTime(p => ({ ...p, hour: v }))}>
                    <SelectTrigger className="h-11 rounded-lg w-[68px]" data-testid="event-end-time-hour">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>{HOURS.map(h => <SelectItem key={h} value={String(h)}>{h}</SelectItem>)}</SelectContent>
                  </Select>
                  <span className="text-lg font-bold text-gray-400">:</span>
                  <Select value={endTime.minute} onValueChange={(v) => setEndTime(p => ({ ...p, minute: v }))}>
                    <SelectTrigger className="h-11 rounded-lg w-[68px]" data-testid="event-end-time-minute">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>{MINUTES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={endTime.period} onValueChange={(v) => setEndTime(p => ({ ...p, period: v }))}>
                    <SelectTrigger className="h-11 rounded-lg w-[72px]" data-testid="event-end-time-period">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AM">AM</SelectItem>
                      <SelectItem value="PM">PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <AddressFields
              value={form.location}
              onChange={(value) => handleChange("location", value)}
              label="Event Address"
              testIdPrefix="event-location-input"
            />
            {adminMode && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-sm font-medium">Host Type</Label>
                  <Select value={hostType} onValueChange={handleHostTypeChange}>
                    <SelectTrigger className="h-11 mt-1 rounded-lg" data-testid="admin-event-host-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">614 Shop Local</SelectItem>
                      <SelectItem value="business">Vendor</SelectItem>
                      <SelectItem value="market">Market</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {hostType !== "admin" && (
                  <div>
                    <Label className="text-sm font-medium">Organization</Label>
                    <Select value={selectedHostId} onValueChange={setSelectedHostId}>
                      <SelectTrigger className="h-11 mt-1 rounded-lg" data-testid="admin-event-host-organization">
                        <SelectValue placeholder={`Select a ${hostType === "market" ? "market" : "vendor"}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {hostOptions.map((organization) => (
                          <SelectItem key={organization.id} value={organization.id}>
                            {getHostName(organization, hostType)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
            <div>
              <Label className="text-sm font-medium">Event Link</Label>
              <Input
                type="text"
                inputMode="url"
                value={form.event_link}
                onChange={(e) => handleChange("event_link", e.target.value)}
                onBlur={(e) => handleChange("event_link", normalizeUrl(e.target.value))}
                placeholder="Tickets, RSVP, or more info link"
                className="h-11 mt-1 rounded-lg"
                data-testid="event-link-input"
              />
              <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Optional. This will appear as a Learn more button on the event.
              </p>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] font-bold mt-2" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="event-submit-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{isEditing || adminMode ? "Saving..." : "Submitting..."}</> : isEditing ? "Save Changes" : adminMode ? "Create Event" : "Submit Event"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
